'use client';

import { useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import {
  Fragment,
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import type { EventData } from 'react-joyride';
import { ACTIONS, EVENTS, Joyride, STATUS } from 'react-joyride';
import { logError } from '../../helpers/logger.helper';
import { hasSaasV1State } from '../../helpers/saas-v1.helper';
import { TOURS, useTourEngineStore } from '../../stores/tour-engine.store';
import { DAY_ZERO_TOUR_ID } from './day-zero-tour.config';
import { TOUR_FLOATING_OPTIONS } from './tour.constants';
import { TourCompletionModal } from './tour-completion-modal.component';
import { TourTooltip } from './tour-tooltip.component';
import { TourWelcomeModal } from './tour-welcome-modal.component';
import { TrialCompletionModal } from './trial-completion-modal.component';
import { TrialWelcomeModal } from './trial-welcome-modal.component';

/** Stop waiting for a step's target after this long and pause the tour. */
const TARGET_WAIT_BAILOUT_MS = 15_000;

/**
 * Grace period before treating a vanished target as a real dismiss. A search
 * result row briefly unmounts while its query re-fetches; only recover once the
 * target has stayed gone this long.
 */
export const TARGET_LOSS_GRACE_MS = 600;

/** The dynamic segment names in a Next.js route pattern (e.g. `organization`). */
const routeParamNames = (route: string): string[] =>
  (route.match(/\[[^\]]+\]/g) ?? []).map((segment) =>
    segment.slice(1, -1).replace(/^\.\.\./, '')
  );

/**
 * Side-effect controller for the guided tour. Reads the tour-engine Zustand
 * store directly (no context), drives route navigation + target-waiting, and
 * renders <Joyride> plus the welcome/completion modals. Mount it once, high in
 * the tree and ABOVE any route-keyed error boundary, so it isn't torn down on
 * client-side navigation (which would blink the overlay and re-run the wait).
 */
export const TourEngineController: FunctionComponent = () => {
  const activeTourId = useTourEngineStore((state) => state.activeTourId);
  const status = useTourEngineStore((state) => state.status);
  const stepIndex = useTourEngineStore((state) => state.stepIndex);
  const advance = useTourEngineStore((state) => state.advance);
  const goBack = useTourEngineStore((state) => state.goBack);
  const recoverToAnchor = useTourEngineStore((state) => state.recoverToAnchor);
  const complete = useTourEngineStore((state) => state.complete);
  const pause = useTourEngineStore((state) => state.pause);
  const dismiss = useTourEngineStore((state) => state.dismiss);

  const activeConfig = activeTourId ? TOURS[activeTourId] : null;
  const activeStep = activeConfig?.steps.at(stepIndex) ?? null;

  const router = useRouter();

  // First-time visitors get Day Zero; returning users get the v2 announcement,
  // which carries its own tour CTA (#8715).
  // NOTE: when the v2 announcement is removed after migration, drop the
  // !hasSaasV1State() gate too, or v1-state browsers are never offered the tour.
  //
  // Gated on router.query.trial rather than just an empty deps array: this
  // component is mounted above (and its effects fire before) the page, so on
  // a cold `?trial=1` landing this effect would otherwise win the race and
  // start Day Zero a moment before useTrialEntry starts the trial tour,
  // clobbering it. Once useTrialEntry strips `?trial=1` from the URL this
  // effect re-evaluates, but by then status is no longer 'idle'.
  useEffect(() => {
    if (!router.isReady || router.query.trial === '1') {
      return;
    }
    const { status, offeredTourIds, start } = useTourEngineStore.getState();
    if (
      status === 'idle' &&
      !offeredTourIds.includes(DAY_ZERO_TOUR_ID) &&
      !hasSaasV1State()
    ) {
      start(DAY_ZERO_TOUR_ID);
    }
  }, [router.isReady, router.query.trial]);

  // Mirror the current step index into a ref so the Joyride callback can read it
  // without going stale — used to distinguish a genuine Next click from
  // react-joyride's echoed STEP_AFTER after an external stepIndex change.
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;

  const theme = useTheme();
  // Latest router, read from effects that shouldn't re-run on every navigation.
  const routerRef = useRef(router);
  routerRef.current = router;
  const [isTargetPresent, setIsTargetPresent] = useState(false);
  // Bumped to re-run the wait-for-target effect and re-anchor Joyride after a
  // step's target is swapped out (e.g. a results sub-tab remounts the tab bar).
  const [reattachNonce, setReattachNonce] = useState(0);
  // The URL where the current step's target was last present (captured in the
  // wait-for-target effect, where presence is confirmed). Recovery returns here
  // rather than to a route rebuilt from params, so query state the page needs —
  // e.g. the opened repository's `?repo=` — survives a wander to a sibling tab.
  const lastValidPathRef = useRef<string | null>(null);

  // Forget that URL when the step changes so recovery can't send the user back
  // to a prior step's page. (A same-step re-run bumps reattachNonce, not
  // activeStep, so the captured URL survives a re-anchor.)
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeStep is the intentional trigger — reset only on a step change
  useEffect(() => {
    lastValidPathRef.current = null;
  }, [activeStep]);

  // Navigate to the route the active step lives on, if we aren't already there.
  useEffect(() => {
    const route = activeStep?.route;
    const href = activeStep?.href;
    if (status !== 'running' || !route || !router.isReady) {
      return;
    }
    // A concrete href pins the step to one exact URL (e.g. the Day Zero recipe),
    // so compare asPath — router.pathname is the dynamic *pattern* and would
    // treat any other recipe page as "already there", never correcting course.
    // A step without an href matches its (static) route by pathname.
    // Compare path portions on both sides: a one-sided strip would never match
    // (so the effect would push on every render) if an href ever carried a query
    // or hash. The config test also asserts hrefs stay bare.
    const onTargetPage = href
      ? router.asPath.split(/[?#]/).at(0) === href.split(/[?#]/).at(0)
      : router.pathname === route;
    if (onTargetPage) {
      return;
    }
    // Prefer the concrete href; otherwise push the route only when it's static.
    // A dynamic route with no href can't be pushed literally — the user reaches
    // it via their own actions and wait-for-target gates the step meanwhile.
    const destination = href ?? (route.includes('[') ? null : route);
    if (!destination) {
      return;
    }
    router.push(destination).catch((error) => {
      // A cancelled / redirected / auth-bounced navigation means the step's
      // route never loads and its target never appears. Pause (and log) now
      // rather than waiting out the full target-wait timeout below.
      logError(
        error instanceof Error
          ? error
          : { name: 'TourRoutePush', message: String(error) }
      );
      pause();
    });
  }, [status, activeStep?.route, activeStep?.href, router, pause]);

  // Wait for the active step's target to exist before letting Joyride render,
  // so a navigation in flight never triggers a TARGET_NOT_FOUND stop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reattachNonce is an intentional trigger to re-detect the target after it's swapped out
  useEffect(() => {
    setIsTargetPresent(false);
    if (status !== 'running' || !activeStep) {
      return;
    }
    const selector =
      typeof activeStep.target === 'string' ? activeStep.target : null;
    if (!selector) {
      setIsTargetPresent(true);
      return;
    }
    // Mark the target present and capture the URL it's valid at (for recovery).
    const markPresent = () => {
      if (routerRef.current.isReady) {
        lastValidPathRef.current = routerRef.current.asPath;
      }
      setIsTargetPresent(true);
    };
    if (document.querySelector(selector)) {
      markPresent();
      return;
    }
    // Target absent — watch for it to (re)appear before doing anything else, so
    // the observer is armed ahead of any navigation we trigger below.
    let bailout: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        markPresent();
        observer.disconnect();
        // Target arrived — cancel the bailout so a slow-loading target (search
        // results, a navigation) doesn't pause the tour once it's actually shown.
        if (bailout) {
          clearTimeout(bailout);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    bailout = setTimeout(() => {
      observer.disconnect();
      // A target that never appears almost always signals a real defect (a
      // renamed data-tour attribute, a page that didn't render) — make it
      // countable instead of letting the tour silently vanish.
      logError({
        name: 'TourTargetNotFound',
        message: `Tour step "${activeStep.id}" target "${selector}" not found after ${TARGET_WAIT_BAILOUT_MS}ms; pausing tour.`
      });
      pause();
    }, TARGET_WAIT_BAILOUT_MS);
    // If the target is missing because we left the step's page for a sibling
    // (e.g. clicking the Visualizations tab away from the Results tab), steer
    // back so it can mount. Prefer the exact URL where the step was last valid
    // (keeps `?repo=` etc.); on first arrival with nothing captured yet, rebuild
    // the step's route from the current params. replace() (not push()) so
    // browser Back isn't trapped. This only fires when the target is missing, so
    // a step whose target persists across those pages (the tab bar) is left
    // alone and exploration stays free. Steps with an `href` are owned by the
    // route-push effect above — skip them here to avoid a competing navigation.
    const liveRouter = routerRef.current;
    const steerTo = (destination: Parameters<typeof liveRouter.replace>[0]) => {
      liveRouter.replace(destination).catch((error) => {
        logError(
          error instanceof Error
            ? error
            : { name: 'TourRouteReconstruct', message: String(error) }
        );
        pause();
      });
    };
    // Steps with an href are owned by the route-push effect above; skip steering
    // them here to avoid a competing navigation.
    const lastValidPath = lastValidPathRef.current;
    const stepRoute = activeStep.route;
    if (!activeStep.href) {
      if (lastValidPath && lastValidPath !== liveRouter.asPath) {
        steerTo(lastValidPath);
      } else if (
        !lastValidPath &&
        stepRoute &&
        liveRouter.isReady &&
        liveRouter.pathname !== stepRoute
      ) {
        const params = routeParamNames(stepRoute);
        const haveAllParams =
          params.length > 0 &&
          params.every((name) => liveRouter.query[name] !== undefined);
        if (haveAllParams) {
          steerTo({ pathname: stepRoute, query: liveRouter.query });
        }
      }
    }
    return () => {
      observer.disconnect();
      clearTimeout(bailout);
    };
  }, [status, activeStep, pause, reattachNonce]);

  // Joyride measures the spotlight once when a step shows and only recomputes on
  // window scroll/resize. Some targets change size afterwards — e.g. the results
  // progress row grows when the sync status + Cancel button render — which would
  // leave the spotlight clipping the new content until the next reflow (the
  // "cut off until you hover" symptom). Observe the active target and nudge
  // Joyride to recompute whenever it resizes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reattachNonce re-observes the fresh node after a re-anchor swaps the target out
  useEffect(() => {
    if (status !== 'running' || !isTargetPresent || !activeStep) {
      return;
    }
    const selector =
      typeof activeStep.target === 'string' ? activeStep.target : null;
    const element = selector ? document.querySelector(selector) : null;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      window.dispatchEvent(new Event('resize'));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [status, isTargetPresent, activeStep, reattachNonce]);

  // Watch the active target for going away out from under the tour. Two cases:
  // (1) a dialog/drawer step (declares resumeAnchor) whose target is dismissed —
  //     rewind to the anchor (or finish, on the last step); and
  // (2) any step whose target node is swapped for a fresh one (a results sub-tab
  //     remounts the shared tab bar) — re-anchor Joyride to the new node instead
  //     of leaving the tooltip stranded at the top-left.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reattachNonce re-arms this observer against the fresh node after a re-anchor
  useEffect(() => {
    const selector =
      typeof activeStep?.target === 'string' ? activeStep.target : null;
    const armedElement = selector ? document.querySelector(selector) : null;
    if (
      status !== 'running' ||
      !isTargetPresent ||
      !selector ||
      !armedElement
    ) {
      return;
    }
    // Pin the step this observer is armed for; act only while it's still current
    // so a late callback can't touch a step we've since moved past.
    const armedIndex = stepIndex;
    const stepRoute = activeStep?.route ?? null;
    const hasAnchor = Boolean(activeStep?.data.resumeAnchor);
    const isLastStep = armedIndex === (activeConfig?.steps.length ?? 0) - 1;
    let pending: ReturnType<typeof setTimeout> | undefined;
    const evaluate = () => {
      if (stepIndexRef.current !== armedIndex) {
        return;
      }
      if (hasAnchor) {
        // Dialog/drawer dismissed: recover only once no matching target remains,
        // debounced so a result row that briefly unmounts mid-refetch is ignored.
        // Fast path — while the armed node is still attached nothing changed, so
        // skip the full-document query these steps would otherwise run on every
        // mutation of their busy pages (live search results, streaming results).
        if (armedElement.isConnected || document.querySelector(selector)) {
          if (pending) {
            clearTimeout(pending);
            pending = undefined;
          }
          return;
        }
        if (pending) {
          return;
        }
        pending = setTimeout(() => {
          pending = undefined;
          if (
            document.querySelector(selector) ||
            stepIndexRef.current !== armedIndex
          ) {
            return;
          }
          observer.disconnect();
          // Closing the drawer on the final step (while still on its page)
          // finishes the tour rather than rewinding to re-open a repository.
          // But a target lost by navigating away (e.g. a side-nav click) must
          // NOT complete — completed is terminal and would drop the user's
          // place — so only finish when still on the step's route.
          const onStepRoute =
            !stepRoute || routerRef.current.pathname === stepRoute;
          if (isLastStep && onStepRoute) {
            complete();
          } else {
            recoverToAnchor();
          }
        }, TARGET_LOSS_GRACE_MS);
        return;
      }
      // Non-anchor step: the armed node was detached (a page/tab swap remounted
      // it). Re-run wait-for-target so Joyride re-anchors to the fresh node.
      if (!armedElement.isConnected) {
        observer.disconnect();
        setReattachNonce((nonce) => nonce + 1);
      }
    };
    const observer = new MutationObserver(evaluate);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (pending) {
        clearTimeout(pending);
      }
    };
  }, [
    status,
    isTargetPresent,
    activeStep,
    stepIndex,
    activeConfig,
    recoverToAnchor,
    complete,
    reattachNonce
  ]);

  const handleJoyrideCallback = useCallback(
    (data: EventData) => {
      const { action, status: joyrideStatus, type } = data;
      if (action === ACTIONS.CLOSE || joyrideStatus === STATUS.SKIPPED) {
        dismiss();
        return;
      }
      // Back button: step to the current step's declared safe re-entry point.
      // Guard on the index (as with NEXT below) so react-joyride's echoed
      // STEP_AFTER after the external stepIndex change doesn't re-trigger it.
      if (
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.PREV &&
        data.index === stepIndexRef.current
      ) {
        goBack();
        return;
      }
      // 'next' steps advance via the Joyride Next button; 'action' steps are
      // advanced by their owning component through the store's advance().
      //
      // In controlled mode, react-joyride ALSO echoes a STEP_AFTER/NEXT for the
      // step we just left whenever stepIndex changes externally (e.g. an action
      // step advanced by its owning component). That echo reports the PREVIOUS
      // index, so only advance when the callback's index matches the step we're
      // currently on — otherwise the echo would double-advance and complete the
      // tour. (See react-joyride componentDidUpdate: isAfterAction && isInitial.)
      if (
        type === EVENTS.STEP_AFTER &&
        action === ACTIONS.NEXT &&
        data.index === stepIndexRef.current
      ) {
        advance();
      }
    },
    [advance, goBack, dismiss]
  );

  return (
    <Fragment>
      <TourWelcomeModal />
      <TrialWelcomeModal />
      <TourCompletionModal />
      <TrialCompletionModal />
      {activeConfig && status === 'running' && isTargetPresent && (
        <Joyride
          steps={activeConfig.steps}
          run
          stepIndex={stepIndex}
          continuous
          onEvent={handleJoyrideCallback}
          tooltipComponent={TourTooltip}
          floatingOptions={TOUR_FLOATING_OPTIONS}
          options={{
            overlayClickAction: false,
            dismissKeyAction: false,
            spotlightRadius: 8,
            zIndex: theme.zIndex.modal + 10,
            arrowColor: theme.palette.background.paper,
            backgroundColor: theme.palette.background.paper,
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            primaryColor: theme.palette.primary.main,
            textColor: theme.palette.text.primary
          }}
        />
      )}
    </Fragment>
  );
};
